import React, { useState, useEffect } from "react"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import { getBrandStateLabel } from "../../../utils/brandStateLabels"
import { defaultStages } from "../../../static/Brand"
import { TAgetAdmins } from "../../../services/brandAPI"
import { X, Calendar, User, Mail, Building2, Filter, ChevronDown, ChevronUp, Check, Brain, AlertTriangle, Zap } from "lucide-react"

const UNASSIGNED_ADMIN_TOKEN = "unassigned"

interface Admin {
  _id: string
  email: string
  name?: string
}

// AI Filter Types
export interface AIFilters {
  ai_status: string[]
  ai_priority: string[]
  ai_min_health_score: number | null
  ai_max_health_score: number | null
  ai_action_required: string
  ai_action_type: string[]
}

interface FiltersProps {
  isOpen: boolean
  onClose: () => void
  handleFetchData: () => void
  params: {
    min_created_at: Date | null
    max_created_at: Date | null
    min_last_login: Date | null
    max_last_login: Date | null
    brand_name: string
    email: string
    first_name: string
    last_name: string
    active_campaigns: string
    stage: string[]
    brand_state: string[]
    responsible_admin: string[]
    process: {
      meeting_planned: string
      information_mail_sent: string
      directed_to_agency_based_work: string
      demo_campaign_created: string
      balance_loaded: string
      campaign_management_continues: string
      campaign_completed: string
      personal_account: string
    }
    classification: string
  }
  setParams: (params: any) => void
  // AI Filters
  aiFilters?: AIFilters
  setAIFilters?: (filters: AIFilters) => void
}

const brandStates = [
  "registered_has_no_campaigns",
  "campaign_in_draft",
  "campaign_created_balance_not_loaded",
  "loaded_balance_has_no_campaigns",
  "campaign_created_has_no_applications",
  "campaign_created_balance_loaded_has_no_applications",
  "campaign_created_received_applications_profile_not_approved",
  "campaign_created_received_applications_profile_approved",
  "campaign_created_received_applications_collaboration_completed",
  "has_balance_loaded",
  "has_balance_not_loaded",
  "loaded_balance_multiple_times",
]

const processOptions = [
  { key: "meeting_planned", label: "Toplantı Planlandı" },
  { key: "information_mail_sent", label: "Bilgilendirme Maili İletildi" },
  { key: "directed_to_agency_based_work", label: "Ajans Bazlı Çalışmaya Yönlendirildi" },
  { key: "demo_campaign_created", label: "Demo Kampanya Oluşturdu" },
  { key: "balance_loaded", label: "Bakiye Yüklemesi Yapıldı" },
  { key: "campaign_management_continues", label: "Kampanya Yönetim Süreci Devam Ediyor" },
  { key: "campaign_completed", label: "Kampanya Tamamlandı" },
  { key: "personal_account", label: "Kişisel Hesap" },
]

// AI Status Options
const aiStatusOptions = [
  { key: "critical", label: "Kritik", icon: "🔴", color: "text-red-600" },
  { key: "warning", label: "Dikkat", icon: "🟠", color: "text-amber-600" },
  { key: "stable", label: "Stabil", icon: "🔵", color: "text-blue-600" },
  { key: "healthy", label: "Sağlıklı", icon: "🟢", color: "text-emerald-600" },
]

// AI Priority Options
const aiPriorityOptions = [
  { key: "P0", label: "P0 - ACİL", color: "bg-red-600 text-white" },
  { key: "P1", label: "P1 - Yüksek", color: "bg-orange-500 text-white" },
  { key: "P2", label: "P2 - Orta", color: "bg-amber-500 text-white" },
  { key: "P3", label: "P3 - Normal", color: "bg-blue-500 text-white" },
  { key: "P4", label: "P4 - Düşük", color: "bg-slate-400 text-white" },
  { key: "P5", label: "P5 - Rutin", color: "bg-slate-300 text-slate-700" },
]

// AI Action Type Options
const aiActionOptions = [
  { key: "outreach", label: "İletişim", icon: "📞" },
  { key: "meeting", label: "Toplantı", icon: "📅" },
  { key: "follow_up", label: "Takip", icon: "↩️" },
  { key: "escalate", label: "Eskalasyon", icon: "⬆️" },
  { key: "support", label: "Destek", icon: "🛠️" },
  { key: "training", label: "Eğitim", icon: "📚" },
  { key: "upsell", label: "Upsell", icon: "💰" },
  { key: "monitor", label: "İzleme", icon: "👁️" },
]

// Default AI Filters
export const defaultAIFilters: AIFilters = {
  ai_status: [],
  ai_priority: [],
  ai_min_health_score: null,
  ai_max_health_score: null,
  ai_action_required: "",
  ai_action_type: [],
}

const CustomerSuccessFilters: React.FC<FiltersProps> = ({
  isOpen,
  onClose,
  handleFetchData,
  params,
  setParams,
  aiFilters = defaultAIFilters,
  setAIFilters,
}) => {
  const [admins, setAdmins] = useState<Admin[]>([])
  const [loadingAdmins, setLoadingAdmins] = useState(false)
  const [expandedSections, setExpandedSections] = useState({
    dates: true,
    search: true,
    aiHealth: true,
    brandState: false,
    stages: false,
    process: false,
    admin: false,
  })

  useEffect(() => {
    if (isOpen) {
      fetchAdmins()
    }
  }, [isOpen])

  const fetchAdmins = async () => {
    try {
      setLoadingAdmins(true)
      const response = await TAgetAdmins()
      setAdmins(response)
    } catch (error) {
      console.error("Error fetching admins:", error)
    } finally {
      setLoadingAdmins(false)
    }
  }

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }))
  }

  const handleClear = () => {
    setParams({
      min_created_at: null,
      max_created_at: null,
      min_last_login: null,
      max_last_login: null,
      brand_name: "",
      email: "",
      first_name: "",
      last_name: "",
      active_campaigns: "",
      stage: [""],
      brand_state: [""],
      responsible_admin: [],
      process: {
        meeting_planned: "",
        information_mail_sent: "",
        directed_to_agency_based_work: "",
        demo_campaign_created: "",
        balance_loaded: "",
        campaign_management_continues: "",
        campaign_completed: "",
        personal_account: "",
      },
      classification: "",
    })
    // Clear AI Filters
    if (setAIFilters) {
      setAIFilters(defaultAIFilters)
    }
  }

  const handleStageToggle = (stage: string) => {
    if (stage === "") {
      setParams({ ...params, stage: [""] })
    } else {
      let stages = params.stage.filter((s) => s !== "")
      const index = stages.indexOf(stage)
      if (index !== -1) {
        stages = stages.filter((s) => s !== stage)
      } else {
        stages = [...stages, stage]
      }
      setParams({ ...params, stage: stages.length > 0 ? stages : [""] })
    }
  }

  const handleBrandStateToggle = (state: string) => {
    if (state === "") {
      setParams({ ...params, brand_state: [""] })
    } else {
      let states = params.brand_state.filter((s) => s !== "")
      const index = states.indexOf(state)
      if (index !== -1) {
        states = states.filter((s) => s !== state)
      } else {
        states = [...states, state]
      }
      setParams({ ...params, brand_state: states.length > 0 ? states : [""] })
    }
  }

  const handleAdminToggle = (email: string) => {
    const currentAdmins = params.responsible_admin || []
    const isSelected = currentAdmins.includes(email)
    const nextAdmins = isSelected
      ? currentAdmins.filter((s) => s !== email)
      : [...currentAdmins, email]
    setParams({ ...params, responsible_admin: nextAdmins })
  }

  const handleProcessToggle = (processKey: string) => {
    const currentValue = (params.process as any)[processKey]
    setParams({
      ...params,
      process: {
        ...params.process,
        [processKey]: currentValue === "" ? "true" : "",
      },
    })
  }

  // AI Filter Handlers
  const handleAIStatusToggle = (status: string) => {
    if (!setAIFilters) {
      return
    }
    const current = aiFilters.ai_status
    const isSelected = current.includes(status)
    const next = isSelected ? current.filter(s => s !== status) : [...current, status]
    setAIFilters({ ...aiFilters, ai_status: next })
  }

  const handleAIPriorityToggle = (priority: string) => {
    if (!setAIFilters) {
      return
    }
    const current = aiFilters.ai_priority
    const isSelected = current.includes(priority)
    const next = isSelected ? current.filter(p => p !== priority) : [...current, priority]
    setAIFilters({ ...aiFilters, ai_priority: next })
  }

  const handleAIActionToggle = (action: string) => {
    if (!setAIFilters) {
      return
    }
    const current = aiFilters.ai_action_type
    const isSelected = current.includes(action)
    const next = isSelected ? current.filter(a => a !== action) : [...current, action]
    setAIFilters({ ...aiFilters, ai_action_type: next })
  }

  const activeFilterCount = () => {
    let count = 0
    if (params.min_created_at || params.max_created_at) { count++ }
    if (params.min_last_login || params.max_last_login) { count++ }
    if (params.brand_name) { count++ }
    if (params.email) { count++ }
    if (params.first_name || params.last_name) { count++ }
    if (params.brand_state[0] !== "") { count++ }
    if (params.stage[0] !== "") { count++ }
    if (params.responsible_admin.length > 0) { count++ }
    Object.values(params.process).forEach((v) => { if (v === "true") { count++ } })
    // AI Filters
    if (aiFilters.ai_status.length > 0) {
      count++
    }
    if (aiFilters.ai_priority.length > 0) {
      count++
    }
    if (aiFilters.ai_action_type.length > 0) {
      count++
    }
    if (aiFilters.ai_min_health_score !== null || aiFilters.ai_max_health_score !== null) {
      count++
    }
    if (aiFilters.ai_action_required === "true") {
      count++
    }
    return count
  }

  if (!isOpen) { return null }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-end">
      <div
        className="absolute inset-0"
        onClick={onClose}
      />
      <div className="relative w-full max-w-md bg-white dark:bg-gray-900 h-full overflow-hidden flex flex-col shadow-2xl animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-r from-primary/5 to-purple-500/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Filter className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Filters</h2>
              {activeFilterCount() > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {activeFilterCount()} active filter{activeFilterCount() > 1 ? "s" : ""}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Quick Date Filters */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4">
            <button
              onClick={() => toggleSection("dates")}
              className="flex items-center justify-between w-full mb-3"
            >
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary" />
                <span className="font-semibold text-gray-900 dark:text-white">Date Range</span>
              </div>
              {expandedSections.dates ? (
                <ChevronUp className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              )}
            </button>
            
            {expandedSections.dates && (
              <>
                <div className="flex flex-wrap gap-2 mb-3">
                  {[
                    { label: "Last 7 days", days: 7 },
                    { label: "Last 30 days", days: 30 },
                    { label: "Last 90 days", days: 90 },
                    { label: "This Year", days: -1 },
                  ].map((filter) => (
                    <button
                      key={filter.label}
                      onClick={() => {
                        const endDate = new Date()
                        let startDate: Date
                        if (filter.days === -1) {
                          startDate = new Date(new Date().getFullYear(), 0, 1)
                        } else {
                          startDate = new Date()
                          startDate.setDate(startDate.getDate() - filter.days)
                        }
                        setParams({
                          ...params,
                          min_last_login: startDate,
                          max_last_login: endDate,
                        })
                      }}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-primary hover:text-primary transition"
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block">
                      From
                    </label>
                    <DatePicker
                      selected={params.min_last_login}
                      onChange={(date) => setParams({ ...params, min_last_login: date })}
                      dateFormat="dd/MM/yyyy"
                      className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/50"
                      placeholderText="Start date"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block">
                      To
                    </label>
                    <DatePicker
                      selected={params.max_last_login}
                      onChange={(date) => setParams({ ...params, max_last_login: date })}
                      dateFormat="dd/MM/yyyy"
                      minDate={params.min_last_login}
                      className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/50"
                      placeholderText="End date"
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Search Fields */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4">
            <button
              onClick={() => toggleSection("search")}
              className="flex items-center justify-between w-full mb-3"
            >
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-primary" />
                <span className="font-semibold text-gray-900 dark:text-white">Search</span>
              </div>
              {expandedSections.search ? (
                <ChevronUp className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              )}
            </button>

            {expandedSections.search && (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block">
                    Brand Name
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={params.brand_name}
                      onChange={(e) => setParams({ ...params, brand_name: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/50"
                      placeholder="Search by brand name..."
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={params.email}
                      onChange={(e) => setParams({ ...params, email: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/50"
                      placeholder="Search by email..."
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={params.first_name}
                      onChange={(e) => setParams({ ...params, first_name: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/50"
                      placeholder="First name"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={params.last_name}
                      onChange={(e) => setParams({ ...params, last_name: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/50"
                      placeholder="Last name"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* AI Health Filters */}
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl p-4 border border-purple-200 dark:border-purple-800">
            <button
              onClick={() => toggleSection("aiHealth")}
              className="flex items-center justify-between w-full"
            >
              <div className="flex items-center gap-2">
                <Brain className="w-4 h-4 text-purple-600" />
                <span className="font-semibold text-gray-900 dark:text-white">AI Health Filters</span>
                {(aiFilters.ai_status.length > 0 || aiFilters.ai_priority.length > 0 || aiFilters.ai_action_type.length > 0) && (
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-purple-500 text-white">
                    {aiFilters.ai_status.length + aiFilters.ai_priority.length + aiFilters.ai_action_type.length}
                  </span>
                )}
              </div>
              {expandedSections.aiHealth ? (
                <ChevronUp className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              )}
            </button>

            {expandedSections.aiHealth && setAIFilters && (
              <div className="mt-4 space-y-4">
                {/* AI Status */}
                <div>
                  <label className="text-xs font-bold text-purple-600 dark:text-purple-400 mb-2 block flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    AI Durum
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {aiStatusOptions.map((status) => (
                      <button
                        key={status.key}
                        onClick={() => handleAIStatusToggle(status.key)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1 ${
                          aiFilters.ai_status.includes(status.key)
                            ? "bg-purple-500 text-white shadow-md"
                            : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-purple-300"
                        }`}
                      >
                        <span>{status.icon}</span>
                        {status.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* AI Priority */}
                <div>
                  <label className="text-xs font-bold text-purple-600 dark:text-purple-400 mb-2 block flex items-center gap-1">
                    <Zap className="w-3 h-3" />
                    Öncelik
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {aiPriorityOptions.map((priority) => (
                      <button
                        key={priority.key}
                        onClick={() => handleAIPriorityToggle(priority.key)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                          aiFilters.ai_priority.includes(priority.key)
                            ? `${priority.color} shadow-md ring-2 ring-purple-300`
                            : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-purple-300"
                        }`}
                      >
                        {priority.key}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Health Score Range */}
                <div>
                  <label className="text-xs font-bold text-purple-600 dark:text-purple-400 mb-2 block">
                    Health Score Aralığı
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        placeholder="Min"
                        value={aiFilters.ai_min_health_score ?? ""}
                        onChange={(e) => setAIFilters({
                          ...aiFilters,
                          ai_min_health_score: e.target.value ? parseInt(e.target.value) : null
                        })}
                        className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                      />
                    </div>
                    <div>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        placeholder="Max"
                        value={aiFilters.ai_max_health_score ?? ""}
                        onChange={(e) => setAIFilters({
                          ...aiFilters,
                          ai_max_health_score: e.target.value ? parseInt(e.target.value) : null
                        })}
                        className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                      />
                    </div>
                  </div>
                  {/* Quick Score Presets */}
                  <div className="flex flex-wrap gap-2 mt-2">
                    <button
                      onClick={() => setAIFilters({ ...aiFilters, ai_min_health_score: null, ai_max_health_score: 30 })}
                      className="px-2 py-1 text-[10px] font-bold bg-red-100 text-red-700 rounded hover:bg-red-200 transition"
                    >
                      0-30 (Kritik)
                    </button>
                    <button
                      onClick={() => setAIFilters({ ...aiFilters, ai_min_health_score: 31, ai_max_health_score: 50 })}
                      className="px-2 py-1 text-[10px] font-bold bg-amber-100 text-amber-700 rounded hover:bg-amber-200 transition"
                    >
                      31-50 (Dikkat)
                    </button>
                    <button
                      onClick={() => setAIFilters({ ...aiFilters, ai_min_health_score: 51, ai_max_health_score: 70 })}
                      className="px-2 py-1 text-[10px] font-bold bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition"
                    >
                      51-70 (Stabil)
                    </button>
                    <button
                      onClick={() => setAIFilters({ ...aiFilters, ai_min_health_score: 71, ai_max_health_score: null })}
                      className="px-2 py-1 text-[10px] font-bold bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200 transition"
                    >
                      71+ (Sağlıklı)
                    </button>
                  </div>
                </div>

                {/* AI Action Type */}
                <div>
                  <label className="text-xs font-bold text-purple-600 dark:text-purple-400 mb-2 block">
                    Önerilen Aksiyon
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {aiActionOptions.map((action) => (
                      <button
                        key={action.key}
                        onClick={() => handleAIActionToggle(action.key)}
                        className={`px-2 py-1 rounded-lg text-[10px] font-bold transition-all flex items-center gap-1 ${
                          aiFilters.ai_action_type.includes(action.key)
                            ? "bg-purple-500 text-white shadow-md"
                            : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:border-purple-300"
                        }`}
                      >
                        <span>{action.icon}</span>
                        {action.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Action Required Toggle */}
                <div>
                  <label
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition border ${
                      aiFilters.ai_action_required === "true"
                        ? "bg-purple-100 dark:bg-purple-900/30 border-purple-300 dark:border-purple-700"
                        : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-purple-300"
                    }`}
                    onClick={() => setAIFilters({
                      ...aiFilters,
                      ai_action_required: aiFilters.ai_action_required === "true" ? "" : "true"
                    })}
                  >
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition ${
                        aiFilters.ai_action_required === "true"
                          ? "bg-purple-500 border-purple-500"
                          : "border-gray-300 dark:border-gray-600"
                      }`}
                    >
                      {aiFilters.ai_action_required === "true" && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Sadece Aksiyon Gerektiren</span>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400">P0, P1, P2 öncelikli müşteriler</p>
                    </div>
                  </label>
                </div>

                {/* Clear AI Filters */}
                {(aiFilters.ai_status.length > 0 || aiFilters.ai_priority.length > 0 || aiFilters.ai_action_type.length > 0 || aiFilters.ai_min_health_score !== null || aiFilters.ai_max_health_score !== null || aiFilters.ai_action_required === "true") && (
                  <button
                    onClick={() => setAIFilters(defaultAIFilters)}
                    className="w-full px-3 py-2 text-xs font-medium text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/30 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/50 transition"
                  >
                    AI Filtreleri Temizle
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Brand State */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4">
            <button
              onClick={() => toggleSection("brandState")}
              className="flex items-center justify-between w-full"
            >
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-gradient-to-r from-green-500 to-blue-500" />
                <span className="font-semibold text-gray-900 dark:text-white">Brand State</span>
                {params.brand_state[0] !== "" && (
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary/10 text-primary">
                    {params.brand_state.filter((s) => s !== "").length}
                  </span>
                )}
              </div>
              {expandedSections.brandState ? (
                <ChevronUp className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              )}
            </button>

            {expandedSections.brandState && (
              <div className="mt-3 space-y-2 max-h-60 overflow-y-auto">
                {brandStates.map((state) => (
                  <label
                    key={state}
                    className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition ${
                      params.brand_state.includes(state)
                        ? "bg-primary/10 border border-primary/20"
                        : "hover:bg-gray-100 dark:hover:bg-gray-700"
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition ${
                        params.brand_state.includes(state)
                          ? "bg-primary border-primary"
                          : "border-gray-300 dark:border-gray-600"
                      }`}
                    >
                      {params.brand_state.includes(state) && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {getBrandStateLabel(state)}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Stages */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4">
            <button
              onClick={() => toggleSection("stages")}
              className="flex items-center justify-between w-full"
            >
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-gradient-to-r from-yellow-500 to-orange-500" />
                <span className="font-semibold text-gray-900 dark:text-white">Stages</span>
                {params.stage[0] !== "" && (
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary/10 text-primary">
                    {params.stage.filter((s) => s !== "").length}
                  </span>
                )}
              </div>
              {expandedSections.stages ? (
                <ChevronUp className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              )}
            </button>

            {expandedSections.stages && (
              <div className="mt-3 space-y-2">
                {defaultStages.map((stage) => (
                  <label
                    key={stage.key}
                    className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition ${
                      params.stage.includes(stage.key)
                        ? "bg-primary/10 border border-primary/20"
                        : "hover:bg-gray-100 dark:hover:bg-gray-700"
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition ${
                        params.stage.includes(stage.key)
                          ? "bg-primary border-primary"
                          : "border-gray-300 dark:border-gray-600"
                      }`}
                    >
                      {params.stage.includes(stage.key) && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <span className="text-sm text-gray-700 dark:text-gray-300">{stage.display}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Process/Operations */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4">
            <button
              onClick={() => toggleSection("process")}
              className="flex items-center justify-between w-full"
            >
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-gradient-to-r from-purple-500 to-pink-500" />
                <span className="font-semibold text-gray-900 dark:text-white">Operations</span>
              </div>
              {expandedSections.process ? (
                <ChevronUp className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              )}
            </button>

            {expandedSections.process && (
              <div className="mt-3 space-y-2">
                {processOptions.map((option) => (
                  <label
                    key={option.key}
                    className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition ${
                      (params.process as any)[option.key] === "true"
                        ? "bg-primary/10 border border-primary/20"
                        : "hover:bg-gray-100 dark:hover:bg-gray-700"
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition ${
                        (params.process as any)[option.key] === "true"
                          ? "bg-primary border-primary"
                          : "border-gray-300 dark:border-gray-600"
                      }`}
                      onClick={() => handleProcessToggle(option.key)}
                    >
                      {(params.process as any)[option.key] === "true" && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <span className="text-sm text-gray-700 dark:text-gray-300">{option.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Responsible Admin */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4">
            <button
              onClick={() => toggleSection("admin")}
              className="flex items-center justify-between w-full"
            >
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-primary" />
                <span className="font-semibold text-gray-900 dark:text-white">Responsible Admin</span>
                {params.responsible_admin.length > 0 && (
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary/10 text-primary">
                    {params.responsible_admin.length}
                  </span>
                )}
              </div>
              {expandedSections.admin ? (
                <ChevronUp className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              )}
            </button>

            {expandedSections.admin && (
              <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                <label
                  className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition ${
                    params.responsible_admin.includes(UNASSIGNED_ADMIN_TOKEN)
                      ? "bg-primary/10 border border-primary/20"
                      : "hover:bg-gray-100 dark:hover:bg-gray-700"
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition ${
                      params.responsible_admin.includes(UNASSIGNED_ADMIN_TOKEN)
                        ? "bg-primary border-primary"
                        : "border-gray-300 dark:border-gray-600"
                    }`}
                    onClick={() => handleAdminToggle(UNASSIGNED_ADMIN_TOKEN)}
                  >
                    {params.responsible_admin.includes(UNASSIGNED_ADMIN_TOKEN) && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <span className="text-sm text-gray-700 dark:text-gray-300 italic">
                    Unassigned
                  </span>
                </label>
                {loadingAdmins ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-primary"></div>
                  </div>
                ) : (
                  admins.map((admin) => (
                    <label
                      key={admin._id}
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition ${
                        params.responsible_admin.includes(admin.email)
                          ? "bg-primary/10 border border-primary/20"
                          : "hover:bg-gray-100 dark:hover:bg-gray-700"
                      }`}
                    >
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition ${
                          params.responsible_admin.includes(admin.email)
                            ? "bg-primary border-primary"
                            : "border-gray-300 dark:border-gray-600"
                        }`}
                        onClick={() => handleAdminToggle(admin.email)}
                      >
                        {params.responsible_admin.includes(admin.email) && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <span className="text-sm text-gray-700 dark:text-gray-300">{admin.email}</span>
                    </label>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <div className="flex gap-3">
            <button
              onClick={handleClear}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition"
            >
              Clear All
            </button>
            <button
              onClick={() => {
                handleFetchData()
                onClose()
              }}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-primary rounded-xl hover:bg-primary/90 transition shadow-lg shadow-primary/25"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CustomerSuccessFilters





